import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const ORANGE = '#ee7203';
const MANUAL = '__manual__';
const normReg = (r) => (r || '').trim().toUpperCase().replace(/\s+/g, '');

const BLANK_FORM = {
  vehicle_registration: '', vehicle_type: 'truck', driver_name: '', driver_phone: '',
  max_weight: 0, cost_per_mile: 0, has_moffet: false,
};

/**
 * Edit mode > "Vehicle" button in the sub header. Adds ONE vehicle to the day being viewed — nothing else.
 *  - pick a vehicle from the branch's vehicle registry (Settings > Vehicles) that is not already on this day, or
 *  - choose "type a vehicle in by hand" for a one-off (hire / borrowed / agency) vehicle that is not in the registry.
 * The vehicle is flagged `day_only: true` and only ever written into this one day's board, so no other day changes
 * (and Settings > Vehicles' clean-up of future days leaves it alone, see transport.py).
 */
export default function AddDayVehicleDialog({ open, onOpenChange, selectedDate, registry, existingVehicles, onAdd }) {
  const [choice, setChoice] = useState('');
  const [form, setForm] = useState(BLANK_FORM);

  useEffect(() => {
    if (open) { setChoice(''); setForm(BLANK_FORM); }
  }, [open]);

  const available = useMemo(
    () => (registry || []).filter((rv) => !!rv && !!rv.id && !(existingVehicles || []).some(
      (v) => v.id === rv.id || v.registry_vehicle_id === rv.id || normReg(v.vehicle_registration) === normReg(rv.vehicle_registration),
    )),
    [registry, existingVehicles],
  );

  const chosenRegistryVehicle = choice && choice !== MANUAL ? (registry || []).find((rv) => rv.id === choice) || null : null;
  const isManual = choice === MANUAL;

  const handleChoose = (value) => {
    setChoice(value);
    if (value === MANUAL) { setForm(BLANK_FORM); return; }
    const rv = (registry || []).find((r) => r.id === value);
    if (rv) {
      setForm({
        vehicle_registration: rv.vehicle_registration || '',
        vehicle_type: rv.vehicle_type || 'truck',
        driver_name: rv.driver_name || '',
        driver_phone: rv.driver_phone || '',
        max_weight: rv.max_weight || 0,
        cost_per_mile: rv.cost_per_mile || 0,
        has_moffet: !!rv.has_moffet,
      });
    }
  };

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleAdd = () => {
    if (!choice) { toast.error('Pick a vehicle, or choose to type one in by hand'); return; }
    const registration = form.vehicle_registration.trim().toUpperCase();
    if (!registration) { toast.error('Vehicle registration is required'); return; }
    if ((existingVehicles || []).some((v) => normReg(v.vehicle_registration) === normReg(registration))) {
      toast.error(`${registration} is already on this day`);
      return;
    }
    // A registry vehicle keeps its registry id (exactly like a vehicle seeded onto the day), so live tracking, ETAs and
    // the Samsara lookups find it. A hand-typed one gets a fresh id and no registry link.
    const base = chosenRegistryVehicle
      ? { ...chosenRegistryVehicle, id: chosenRegistryVehicle.id, registry_vehicle_id: chosenRegistryVehicle.id }
      : { id: `vehicle_${Date.now()}`, color_code: 'blue', contract_hire: 0, maintenance_insurance: 0, moffet: 0, driver_cost: 0 };
    onAdd({
      ...base,
      vehicle_registration: registration,
      vehicle_type: form.vehicle_type,
      driver_name: form.driver_name.trim(),
      driver_phone: form.driver_phone.trim(),
      max_weight: Number(form.max_weight) || 0,
      cost_per_mile: Number(form.cost_per_mile) || 0,
      has_moffet: !!form.has_moffet,
      moffet_override: null,
      entries: [],
      runs: [],
      total_miles: 0,
      total_weight: 0,
      off_road: false,
      is_full: false,
      day_only: true,
    });
  };

  let dayLabel = 'this day';
  try { dayLabel = format(selectedDate, 'EEEE d MMMM yyyy'); } catch { /* keep the generic label */ }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white border-2 border-orange-200" data-testid="transport-new-add-vehicle-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2" style={{ color: ORANGE }}>
            <Truck className="w-7 h-7" />Add Vehicle To This Day
          </DialogTitle>
          <DialogDescription>
            Adds a vehicle to <strong>{dayLabel}</strong> only. No other day is changed.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="col-span-2">
            <Label>Vehicle</Label>
            <Select value={choice} onValueChange={handleChoose}>
              <SelectTrigger data-testid="transport-new-add-vehicle-choice" className="mt-1 font-bold">
                <SelectValue placeholder="Pick a vehicle from this branch, or type one in by hand…" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {available.map((rv) => (
                  <SelectItem key={rv.id} value={rv.id}>
                    {rv.vehicle_registration}{rv.driver_name ? ` — ${rv.driver_name}` : ''}{rv.is_default === false ? ' (non-default)' : ''}
                  </SelectItem>
                ))}
                <SelectItem value={MANUAL}>✎ Type a vehicle in by hand (this day only)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {available.length === 0
                ? 'Every vehicle on the system for this branch is already on this day. You can still type one in by hand.'
                : 'Vehicles already on this day are not listed. Details below can be changed for this day only.'}
            </p>
          </div>

          {choice && (
            <>
              <div>
                <Label>Vehicle Registration *</Label>
                <Input
                  data-testid="transport-new-vehicle-reg-input"
                  value={form.vehicle_registration}
                  disabled={!isManual}
                  onChange={(e) => set('vehicle_registration', e.target.value.toUpperCase())}
                  placeholder="e.g. VX24 YXK"
                  className="mt-1 font-bold"
                />
              </div>
              <div>
                <Label>Vehicle Type</Label>
                <Select value={form.vehicle_type} onValueChange={(v) => set('vehicle_type', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Driver Name</Label>
                <Input data-testid="transport-new-driver-name-input" value={form.driver_name} onChange={(e) => set('driver_name', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Driver Phone</Label>
                <Input data-testid="transport-new-driver-phone-input" value={form.driver_phone} onChange={(e) => set('driver_phone', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Max Weight</Label>
                <Input type="number" step="0.1" value={form.max_weight} onChange={(e) => set('max_weight', parseFloat(e.target.value) || 0)} className="mt-1" />
              </div>
              <div>
                <Label>Cost Per Mile (£)</Label>
                <Input type="number" step="0.01" value={form.cost_per_mile} onChange={(e) => set('cost_per_mile', parseFloat(e.target.value) || 0)} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label>Moffet</Label>
                <Select value={form.has_moffet ? 'yes' : 'no'} onValueChange={(v) => set('has_moffet', v === 'yes')}>
                  <SelectTrigger data-testid="transport-new-add-vehicle-moffet-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="no">No Moffet</SelectItem>
                    <SelectItem value="yes">Has Moffet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="transport-new-save-vehicle-btn" onClick={handleAdd} disabled={!choice} className="text-white font-semibold" style={{ backgroundColor: ORANGE }}>
            Add Vehicle
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
